import { useEffect, useRef, useState } from 'react';

import { Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps, SitecoreContextValue } from 'src/lib/ComponentProps';

import { Button } from 'src/components/basicContent/_Button/Button';

import PDFIconSvg from 'public/icons/pdf.svg';

import { AllResourceUrls } from '../ContentOverviewToolbarFields';

import DownloadOptions from './DownloadOptions/DownloadOptions';

import styles from './DownloadButton.module.css';

type DownloadButtonProps = ComponentProps & {
	fields: {
		allResourceUrls?: AllResourceUrls[];
		currentResourceUrl?: string;
	};
};

export default function DownloadButton(props: DownloadButtonProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const [showDownloadOptions, setShowDownloadOptions] = useState(false);

	const selectDownloadButton = useRef<HTMLButtonElement>(null);
	const downloadOptionsRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleClickOutsideDownloadButtonContainer(event: MouseEvent): void {
			if (downloadOptionsRef.current?.contains(event.target as Node)) {
				setShowDownloadOptions(true);
			} else {
				setShowDownloadOptions(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutsideDownloadButtonContainer);
		return () => document.removeEventListener('mousedown', handleClickOutsideDownloadButtonContainer);
	}, []);

	return (
		<div className={styles.downloadButtonContainer}>
			<Button
				title={sitecoreContext.dictionary?.downloadButtonTooltip}
				buttonRef={selectDownloadButton}
				className={`${styles.selectDownload} link linkOffset linkHoverWeighted`}
				onClick={() => setShowDownloadOptions(!showDownloadOptions)}
				onBlur={(e: React.FocusEvent<HTMLButtonElement, Element> | undefined) => {
					setShowDownloadOptions(false);

					downloadOptionsRef.current?.childNodes.forEach((elem: HTMLElement) => {
						elem.childNodes[0]?.childNodes.forEach((elem: HTMLElement) => {
							if (e?.relatedTarget === elem) {
								setShowDownloadOptions(true);
							}
						});
					});
				}}
			>
				<PDFIconSvg className={styles.selectDownloadIcon} alt={sitecoreContext.dictionary?.downloadSinglePage} />

				<span>
					<Text field={{ value: sitecoreContext.dictionary?.downloadButtonText }} />
				</span>
			</Button>

			<div ref={downloadOptionsRef}>
				<DownloadOptions
					showDownloadOptions={showDownloadOptions}
					allResourceUrls={props.fields?.allResourceUrls}
					currentResourceUrl={props.fields?.currentResourceUrl}
					hideDownloadOptions={() => setShowDownloadOptions(false)}
				/>
			</div>
		</div>
	);
}
